import { useCallback, useEffect, useState, startTransition } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { IntroNote } from "@/components/IntroNote";
import { Sparkline } from "@/components/Sparkline";
import { OwnerNotificationsCard } from "@/components/OwnerNotificationsCard";
import { PdfAssetFailuresBanner } from "@/components/PdfAssetFailuresBanner";
import { DriftSavingsPanel } from "@/components/DriftSavingsPanel";
import { SystemHealth } from "@/components/SystemHealth";
import { RemediationSavingsPanel } from "@/components/RemediationSavingsPanel";
import { LeadForwarderHealthPanel } from "@/components/LeadForwarderHealthPanel";
import { MappingAuditAggregatePanel } from "@/components/MappingAuditAggregatePanel";
import { TrustedOriginKeyHandoverPanel } from "@/components/TrustedOriginKeyHandoverPanel";
import { OutboundWebhooksPanel } from "@/components/OutboundWebhooksPanel";
import { VoiceSpendPanel } from "@/components/VoiceSpendPanel";
import { OwnerSalesDocsPanel } from "@/components/OwnerSalesDocsPanel";
import { OwnerInboundFunnelPanel } from "@/components/OwnerInboundFunnelPanel";
import { ReportsVerifyPanel } from "@/components/ReportsVerifyPanel";
import { CloudProvidersPanel } from "@/components/CloudProvidersPanel";
import { CatalogueDemandPanel } from "@/components/CatalogueDemandPanel";
import {
  AutomationKillSwitchPanel,
  BriefingWebhooksPanel,
  BoardPackEngagementPanel,
  DemoLoopRefreshPanel,
  RoiHotLeadsPanel,
  PilotHealthPanel,
  TenantConnectorQuotaPanel,
  CheckinHistoryPanel,
} from "@/components/OwnerAutomationPanels";
import { BenchmarksRefreshPanel } from "@/components/BenchmarksRefreshPanel";
import { useCollapsible } from "@/components/CollapsibleList";
import { ManageTenantDialog } from "./tenants/ManageTenantDialog";
import { TenantConfigPanel } from "@/components/TenantConfigPanel";
import {
  Building2, Plus, Loader2, Globe, Layers, RefreshCw, ShieldCheck, Users, Activity,
  Flame, FileDown, Gauge, ClipboardCheck, Sparkles, Eraser, Settings, Power, Settings2 as ConfigIcon,
  AlertTriangle, Copy, Check, Eye, EyeOff, CheckCircle2, FileText,
} from "lucide-react";

const EMPTY = {
  tenant_id: "",
  name: "",
  admin_email: "",
  admin_password: "",
  region: "GB",
  locale: "en",
  sectors: "",
  clone_starter: true,
};

// Compact inline SVG sparkline for the 8-week adoption trajectory. No deps.
function AdoptionSparkline({ trend, tenantId }) {
  if (!Array.isArray(trend) || trend.length < 2) return null;
  const w = 120;
  const h = 28;
  const pad = 2;
  const max = Math.max(...trend, 1);
  const min = Math.min(...trend, 0);
  const span = max - min || 1;
  const stepX = (w - pad * 2) / (trend.length - 1);
  const pts = trend.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - ((v - min) / span) * (h - pad * 2);
    return [x, y];
  });
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${pad},${h - pad} ${line} ${(w - pad).toFixed(1)},${h - pad}`;
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible"
      role="img" aria-label={`Adoption over the last ${trend.length} weeks`}
      data-testid={`tenant-adoption-spark-${tenantId}`}>
      <polygon points={area} fill="rgba(119,55,246,0.10)" />
      <polyline points={line} fill="none" stroke="#7737f6" strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2.4" fill="#7737f6" />
    </svg>
  );
}

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [usage, setUsage] = useState(null);
  const [hotLeads, setHotLeads] = useState(null);
  const [srlHotLeads, setSrlHotLeads] = useState(null);
  const [utmRollup, setUtmRollup] = useState(null);
  const [permOverdue, setPermOverdue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [manageId, setManageId] = useState(null);  // tenant_id of the open Manage drawer
  const [configId, setConfigId] = useState(null);  // tenant_id of the open Configure drawer
  const [lastProvisioned, setLastProvisioned] = useState(null);  // {tenant_id, name, admin_email, admin_password, bootstrap, starter_pack}

  const load = useCallback(() => {
    // React 19's `react-hooks/set-state-in-effect` rule complains when
    // a setter is invoked synchronously inside an effect; we want the
    // loading spinner up before the network calls fire, so wrap the
    // state flip in startTransition to defer it cleanly.
    startTransition(() => setLoading(true));
    api.get("/tenants")
      .then(({ data }) => setTenants(data.items || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load tenants"))
      .finally(() => setLoading(false));
    api.get("/analytics/summary?days=30")
      .then(({ data }) => setUsage(data))
      .catch(() => setUsage(null));
    api.get("/roi/hot-leads")
      .then(({ data }) => setHotLeads(data))
      .catch(() => setHotLeads(null));
    api.get("/leads/hot/supplier-risk-lens?min_failing=3&limit=10")
      .then(({ data }) => setSrlHotLeads(data))
      .catch(() => setSrlHotLeads(null));
    api.get("/leads/utm-rollup?source=supplier-risk-lens&days=30")
      .then(({ data }) => setUtmRollup(data))
      .catch(() => setUtmRollup(null));
    // iter122 — SOC 2 permanent-grants overdue tile. Surfaces the
    // backlog without making the IP-owner open the docs admin panel.
    api.get("/docs/permanent-grants-overdue")
      .then(({ data }) => setPermOverdue(data))
      .catch(() => setPermOverdue(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const refreshQuality = async () => {
    setSnapshotting(true);
    try {
      const { data } = await api.post("/tenants/quality-snapshot");
      toast.success(`Quality snapshot refreshed for ${data.snapshotted || 0} tenant(s).`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Snapshot refresh failed");
    } finally {
      setSnapshotting(false);
    }
  };

  const seedDemoTrend = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/tenants/quality-snapshot/demo-backfill", { weeks: 8 });
      toast.success(`Seeded ${data.seeded || 0} synthetic point(s) across ${data.tenants || 0} tenant(s) — sparklines now render.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Demo backfill failed");
    } finally {
      setSeeding(false);
    }
  };

  const clearSynthetic = async () => {
    if (!window.confirm("Remove all synthetic demo snapshots and revert to real history only?")) return;
    setClearing(true);
    try {
      const { data } = await api.delete("/tenants/quality-snapshot/synthetic");
      toast.success(`Cleared ${data.cleared || 0} synthetic snapshot(s).`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Clear failed");
    } finally {
      setClearing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...form,
        sectors: form.sectors ? form.sectors.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      const { data } = await api.post("/tenants", payload);
      toast.success(`Tenant "${data.name}" provisioned (${data.starter_pack?.controls || 0} controls, ${data.starter_pack?.kris || 0} KRIs cloned)`);
      // Surface the post-provisioning context so the Owner can hand the
      // customer the temp password + a downloadable pilot brief without
      // needing the CLI script. We deliberately keep this in-memory only
      // — refreshing the page or clicking Dismiss clears the password.
      setLastProvisioned({
        tenant_id: data.tenant_id || payload.tenant_id,
        name: data.name || payload.name,
        admin_email: data.admin_email || payload.admin_email,
        admin_name: payload.admin_name || `${payload.name} Admin`,
        admin_password: payload.admin_password,
        starter_pack: data.starter_pack || {},
        bootstrap_email: data.bootstrap_email || { sent: false },
      });
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Provisioning failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <Seo title="Tenants" description="Provision and manage isolated ERIO tenants." path="/tenants" />
      <main className="mx-auto max-w-5xl px-6 py-8" data-testid="tenants-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="ek-eyebrow">Owner · Platform</p>
            <h1 className="ek-heading mt-1 text-3xl">Tenants</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={seedDemoTrend} disabled={seeding}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-60"
              data-testid="tenants-demo-backfill"
              title="Seed 8 weeks of synthetic past snapshots so the sparklines render immediately — demo only">
              {seeding ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Seed demo trend
            </button>
            <button onClick={clearSynthetic} disabled={clearing}
              className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium text-ek-ink hover:bg-ek-purple/5 disabled:opacity-60"
              data-testid="tenants-clear-synthetic"
              title="Remove every synthetic demo snapshot and revert to real weekly history only">
              {clearing ? <Loader2 size={15} className="animate-spin" /> : <Eraser size={15} />} Clear demo
            </button>
            <button onClick={refreshQuality} disabled={snapshotting}
              className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium text-ek-ink hover:bg-ek-purple/5 disabled:opacity-60"
              data-testid="tenants-refresh-quality"
              title="Compute a fresh PIS+RRQS snapshot for every tenant right now">
              {snapshotting ? <Loader2 size={15} className="animate-spin" /> : <Gauge size={15} />} Refresh quality snapshot
            </button>
            <button onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium text-ek-ink hover:bg-ek-purple/5" data-testid="tenants-refresh">
              <RefreshCw size={15} /> Refresh
            </button>
            <button onClick={() => setShowForm((s) => !s)} className="ek-btn flex items-center gap-1.5" data-testid="tenant-new-toggle">
              <Plus size={15} /> New tenant
            </button>
          </div>
        </div>

        {/* Quick-jump chips — most-used Owner surfaces on this page */}
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="tenants-quick-jump">
          <span className="text-[11px] uppercase tracking-wider text-ek-ink/50">Jump to:</span>
          <a
            href="#sales-docs"
            className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-200 bg-fuchsia-50 px-3 py-1 text-xs font-semibold text-fuchsia-700 hover:bg-fuchsia-100"
            data-testid="tenants-jump-sales-docs"
          >
            <FileText size={12} /> Sales, partner & strategy docs
          </a>
        </div>

        <IntroNote className="mt-4">
          Each tenant is fully isolated — its risks, suppliers, controls and KRIs never mix with another&apos;s. Provisioning
          clones the platform control &amp; KRI libraries plus risk-appetite defaults so the new tenant is usable
          immediately, and creates its org-admin (one-time password).
        </IntroNote>

        {usage && (
          <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="usage-summary">
            <div className="rounded-xl border border-black/5 bg-white p-4">
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-ek-ink/65"><Users size={12} /> Active users · 30d</p>
              <p className="mt-1 font-display text-2xl font-bold text-ek-ink" data-testid="usage-active-users">{usage.active_users}</p>
            </div>
            <div className="rounded-xl border border-black/5 bg-white p-4">
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-ek-ink/65"><Activity size={12} /> Events · 30d</p>
              <p className="mt-1 font-display text-2xl font-bold text-ek-ink" data-testid="usage-total-events">{usage.total_events}</p>
            </div>
            <div className="rounded-xl border border-black/5 bg-white p-4">
              <p className="text-[11px] uppercase tracking-wide text-ek-ink/65">Top activity</p>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(usage.top_events || []).slice(0, 4).map((e) => (
                  <span key={e.event} className="rounded-full bg-ek-purple/10 px-2 py-0.5 text-[11px] text-ek-purple">{e.event} · {e.count}</span>
                ))}
                {(!usage.top_events || usage.top_events.length === 0) && <span className="text-xs text-ek-ink/65">No activity yet</span>}
              </div>
            </div>
          </div>
        )}

        <OwnerNotificationsCard />
        <PdfAssetFailuresBanner />
        <LeadForwarderHealthPanel />
        <TrustedOriginKeyHandoverPanel />
        <AutomationKillSwitchPanel />
        <OutboundWebhooksPanel />
        <BriefingWebhooksPanel />
        <BoardPackEngagementPanel />
        <BenchmarksRefreshPanel />
        <PilotHealthPanel />
        <CheckinHistoryPanel />
        <TenantConnectorQuotaPanel />
        <VoiceSpendPanel />
        <OwnerSalesDocsPanel />
        <ReportsVerifyPanel />
        <CloudProvidersPanel />
        <CatalogueDemandPanel />
        <OwnerInboundFunnelPanel />
        <DemoLoopRefreshPanel />
        <RoiHotLeadsPanel />
        <MappingAuditAggregatePanel />
        <DriftSavingsPanel />
        <SystemHealth />
        <RemediationSavingsPanel />
        <HotLeadsPanel hotLeads={hotLeads} />
        <SrlHotLeadsPanel data={srlHotLeads} />
        <UtmRollupCard data={utmRollup} />
        <PermanentOverduePanel data={permOverdue} />

        {lastProvisioned && (
          <PostProvisionCard
            ctx={lastProvisioned}
            onDismiss={() => setLastProvisioned(null)}
          />
        )}

        {showForm && (
          <form onSubmit={submit} className="mt-6 rounded-2xl border border-black/5 bg-white p-6" data-testid="tenant-create-form">
            <h2 className="font-display text-lg font-semibold text-ek-ink">Provision a new tenant</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Tenant ID" testid="tenant-field-tenant_id" value={form.tenant_id} onChange={(v) => { setForm(f => ({ ...f, tenant_id: v, _id_edited: true })); }} placeholder="tnt_acme" required />
              <Field label="Tenant name" testid="tenant-field-name" value={form.name} onChange={(v) => {
                // Auto-derive a tenant_id from the name on first edit (until the user manually edits the id).
                if (!form._id_edited) {
                  const slug = (v || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 24);
                  const suffix = Math.random().toString(36).slice(2, 6);
                  set("tenant_id", `tnt_${slug || "new"}_${suffix}`);
                }
                set("name", v);
              }} placeholder="Acme Holdings" required />
              <Field label="Org-admin email" testid="tenant-field-admin_email" type="email" value={form.admin_email} onChange={(v) => set("admin_email", v)} placeholder="admin@acme.com" required />
              <div data-testid="tenant-field-admin_password-wrapper" className="block text-xs">
                <span className="font-semibold text-ek-ink/85">Org-admin password <span className="text-rose-500">*</span></span>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text" required minLength={8}
                    value={form.admin_password}
                    onChange={(e) => set("admin_password", e.target.value)}
                    placeholder="≥ 8 characters"
                    className="w-full rounded-md border border-black/10 bg-white px-2.5 py-1.5 text-sm text-ek-ink focus:border-ek-purple/50 focus:outline-none"
                    data-testid="tenant-field-admin_password"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      // Match `scripts/provision_pilot.py` — 20 char,
                      // lowercase + uppercase + digit + symbol guaranteed.
                      const lower = "abcdefghjkmnpqrstuvwxyz";
                      const upper = "ABCDEFGHJKMNPQRSTUVWXYZ";
                      const digits = "23456789";
                      const symbols = "!#$%^&*";
                      const all = lower + upper + digits + symbols;
                      const pick = (s) => s[Math.floor(Math.random() * s.length)];
                      let p = pick(lower) + pick(upper) + pick(digits) + pick(symbols);
                      for (let i = 0; i < 16; i += 1) p += pick(all);
                      // Fisher-Yates to spread the required-class chars.
                      const arr = p.split("");
                      for (let i = arr.length - 1; i > 0; i -= 1) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [arr[i], arr[j]] = [arr[j], arr[i]];
                      }
                      set("admin_password", arr.join(""));
                    }}
                    className="shrink-0 rounded-md border border-black/10 px-2.5 text-xs font-semibold text-ek-ink hover:border-ek-purple/40 hover:text-ek-purple"
                    data-testid="tenant-field-admin_password-gen"
                  >Generate</button>
                </div>
                <span className="mt-1 block text-[11px] text-ek-ink/50">User will be forced to change this on first login.</span>
              </div>
              <Field label="Region" testid="tenant-field-region" value={form.region} onChange={(v) => set("region", v)} placeholder="GB" />
              <Field label="Locale" testid="tenant-field-locale" value={form.locale} onChange={(v) => set("locale", v)} placeholder="en" />
              <Field label="Sectors (comma-separated)" testid="tenant-field-sectors" value={form.sectors} onChange={(v) => set("sectors", v)} placeholder="financial_services, manufacturing" className="sm:col-span-2" />
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-ek-ink/70">
              <input type="checkbox" checked={form.clone_starter} onChange={(e) => set("clone_starter", e.target.checked)} data-testid="tenant-field-clone" />
              Clone the starter pack (control library, KRI definitions, risk-appetite defaults)
            </label>
            <div className="mt-5 flex items-center gap-3">
              <button type="submit" disabled={creating} className="ek-btn flex items-center gap-1.5" data-testid="tenant-create-submit">
                {creating ? <Loader2 size={15} className="animate-spin" /> : <Building2 size={15} />} Provision tenant
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="text-sm text-ek-ink/65 hover:text-ek-ink">Cancel</button>
            </div>
          </form>
        )}

        <div className="mt-8" data-testid="tenants-list">
          {loading ? (
            <p className="text-sm text-ek-ink/65">Loading tenants…</p>
          ) : tenants.length === 0 ? (
            <p className="text-sm text-ek-ink/65" data-testid="tenants-empty">No tenants yet.</p>
          ) : (
            <TenantsGrid tenants={tenants} setManageId={setManageId} setConfigId={setConfigId} />
          )}
        </div>
        {manageId && (
          <ManageTenantDialog
            tenantId={manageId}
            onClose={() => setManageId(null)}
            onChanged={() => { setManageId(null); load(); }}
          />
        )}
        {configId && (
          <ConfigureTenantDrawer
            tenant={tenants.find((t) => t.tenant_id === configId)}
            onClose={() => setConfigId(null)}
          />
        )}
      </main>
    </div>
  );
}


function TenantsGrid({ tenants, setManageId, setConfigId }) {
  const { visible, Toggle } = useCollapsible(tenants, 5, "tenants");
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        {visible.map((t) => (
              <div key={t.tenant_id} className={`rounded-2xl border bg-white p-5 ${t.active === false ? "border-red-200 bg-red-50/40" : "border-black/5"}`} data-testid={`tenant-card-${t.tenant_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Building2 size={18} className="text-ek-purple" />
                    <h3 className="font-display text-base font-semibold text-ek-ink">{t.name}</h3>
                    {t.active === false && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700"
                        data-testid={`tenant-disabled-badge-${t.tenant_id}`}>
                        <Power size={10} /> Disabled
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setConfigId(t.tenant_id)}
                      className="inline-flex items-center gap-1 rounded-md border border-ek-purple/30 bg-ek-purple/5 px-2 py-1 text-[11px] font-medium text-ek-purple hover:bg-ek-purple/10"
                      data-testid={`tenant-configure-${t.tenant_id}`}
                      title="Switch connectors, set infrastructure tier, capture size & locale">
                      <ConfigIcon size={11} /> Configure
                    </button>
                    <button onClick={() => setManageId(t.tenant_id)}
                      className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2 py-1 text-[11px] font-medium text-ek-ink hover:bg-ek-purple/5"
                      data-testid={`tenant-manage-${t.tenant_id}`}
                      title="Edit metadata, re-seed starter pack, enable/disable">
                      <Settings size={11} /> Manage
                    </button>
                  </div>
                </div>
                <p className="mt-1 font-mono text-xs text-ek-ink/65">{t.tenant_id}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-ek-ink/65">
                  {t.region && <span className="inline-flex items-center gap-1 rounded-full bg-ek-canvas px-2 py-0.5"><Globe size={11} /> {t.region}</span>}
                  {t.locale && <span className="inline-flex items-center gap-1 rounded-full bg-ek-canvas px-2 py-0.5">{t.locale}</span>}
                  {(t.sectors || []).map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 rounded-full bg-ek-purple/10 px-2 py-0.5 text-ek-purple"><Layers size={11} /> {s}</span>
                  ))}
                </div>
                <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-emerald-700">
                  <ShieldCheck size={12} /> Isolated tenant
                </p>
                {t.connector_counts && (
                  <p className="mt-1.5 inline-flex flex-wrap items-center gap-1.5 text-[11px]"
                     data-testid={`tenant-connector-counts-${t.tenant_id}`}>
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                      {t.connector_counts.free} free
                    </span>
                    <span className="rounded-full bg-ek-purple/10 px-2 py-0.5 font-semibold text-ek-purple">
                      {t.connector_counts.premium} premium
                    </span>
                    {t.connector_counts.scoped && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700"
                            title="Owner has narrowed the connector list for this tenant">
                        scoped
                      </span>
                    )}
                  </p>
                )}
                {t.activation && (
                  <div className="mt-3 border-t border-black/5 pt-3" data-testid={`tenant-adoption-${t.tenant_id}`}>
                    <div className="flex items-center justify-between text-[11px] text-ek-ink/65">
                      <span className="uppercase tracking-wide">Adoption</span>
                      <span className="flex items-center gap-1.5 font-semibold text-ek-ink">
                        {typeof t.activation.delta_pct === "number" && t.activation.delta_pct !== 0 && (
                          <span className={t.activation.delta_pct > 0 ? "text-emerald-700" : "text-red-700"}
                            data-testid={`tenant-adoption-delta-${t.tenant_id}`}>
                            {t.activation.delta_pct > 0 ? "▲" : "▼"} {Math.abs(t.activation.delta_pct)}
                          </span>
                        )}
                        {t.activation.engaged}/{t.activation.users} engaged · avg {t.activation.avg_steps}/{t.activation.max_steps} steps
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ek-purple/10">
                      <div className="h-full rounded-full bg-ek-purple transition-all duration-500"
                        style={{ width: `${t.activation.adoption_pct}%` }} />
                    </div>
                    <p className="mt-1 text-right text-[11px] font-semibold text-ek-purple" data-testid={`tenant-adoption-pct-${t.tenant_id}`}>
                      {t.activation.adoption_pct}%
                    </p>
                    {Array.isArray(t.activation.trend) && t.activation.trend.length >= 2 ? (
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-ek-ink/65">8-week trend</span>
                        <AdoptionSparkline trend={t.activation.trend} tenantId={t.tenant_id} />
                      </div>
                    ) : (
                      <p className="mt-2 text-[10px] text-ek-ink/65" data-testid={`tenant-adoption-trend-pending-${t.tenant_id}`}>
                        Weekly trend builds as the adoption digest runs.
                      </p>
                    )}
                  </div>
                )}
                <QualityBlock tenant={t} />
              </div>
            ))}
            <Toggle className="mt-3" />
        </div>
      </>
  );
}


function QualityBlock({ tenant }) {
  const q = tenant?.quality;
  if (!q) return null;
  const pisLevel = q.pis_latest?.level;
  const rrqsLevel = q.rrqs_latest?.level;
  const pisScore = q.pis_latest?.score;
  const rrqsScore = q.rrqs_latest?.score;
  const hasTrend = (q.pis_trend?.length || 0) >= 2 || (q.rrqs_trend?.length || 0) >= 2;
  return (
    <div className="mt-3 border-t border-black/5 pt-3"
         data-testid={`tenant-quality-${tenant.tenant_id}`}>
      <div className="flex items-center justify-between text-[11px] text-ek-ink/65">
        <span className="inline-flex items-center gap-1 uppercase tracking-wide">
          <Gauge size={11} /> Quality
        </span>
        <span className="text-[10px] text-ek-ink/45">
          {q.snapshot_count > 0 ? `${q.snapshot_count} weekly snapshot${q.snapshot_count === 1 ? "" : "s"}` : "no snapshots yet"}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-black/5 bg-ek-canvas/40 p-2"
             data-testid={`tenant-pis-${tenant.tenant_id}`}>
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-ek-ink/70">
              <ClipboardCheck size={11} /> PIS
            </span>
            {typeof pisScore === "number" && (
              <span className="text-[11px] font-bold text-ek-ink"
                    data-testid={`tenant-pis-score-${tenant.tenant_id}`}>
                {pisScore.toFixed(1)}
                {pisLevel && <span className="ml-1 text-[10px] font-medium text-ek-ink/55">L{pisLevel}</span>}
              </span>
            )}
          </div>
          <div className="mt-1.5">
            {(q.pis_trend?.length || 0) >= 2 ? (
              <Sparkline values={q.pis_trend} width={130} height={26} color="#7737f6"
                ariaLabel="Plan Integrity Score trend"
                testid={`tenant-pis-spark-${tenant.tenant_id}`} />
            ) : (
              <p className="text-[10px] text-ek-ink/55"
                 data-testid={`tenant-pis-spark-pending-${tenant.tenant_id}`}>
                Builds weekly.
              </p>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-black/5 bg-ek-canvas/40 p-2"
             data-testid={`tenant-rrqs-${tenant.tenant_id}`}>
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-ek-ink/70">
              <ShieldCheck size={11} /> RRQS
            </span>
            {typeof rrqsScore === "number" && (
              <span className="text-[11px] font-bold text-ek-ink"
                    data-testid={`tenant-rrqs-score-${tenant.tenant_id}`}>
                {rrqsScore.toFixed(1)}
                {rrqsLevel && <span className="ml-1 text-[10px] font-medium text-ek-ink/55">L{rrqsLevel}</span>}
              </span>
            )}
          </div>
          <div className="mt-1.5">
            {(q.rrqs_trend?.length || 0) >= 2 ? (
              <Sparkline values={q.rrqs_trend} width={130} height={26} color="#0ea5e9"
                ariaLabel="Risk Register Quality Score trend"
                testid={`tenant-rrqs-spark-${tenant.tenant_id}`} />
            ) : (
              <p className="text-[10px] text-ek-ink/55"
                 data-testid={`tenant-rrqs-spark-pending-${tenant.tenant_id}`}>
                Builds weekly.
              </p>
            )}
          </div>
        </div>
      </div>
      {!hasTrend && (
        <p className="mt-2 text-[10.5px] text-ek-ink/55"
           data-testid={`tenant-quality-empty-${tenant.tenant_id}`}>
          Use &ldquo;Refresh quality snapshot&rdquo; to seed a trend point now — otherwise builds weekly on Monday 08:15 UTC.
        </p>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, placeholder, type = "text", required, testid, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-medium text-ek-ink/60">{label}</span>
    <input
      type={type}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm"
    />
  </label>
);

const gbp = (x) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(x || 0);

const relativeTime = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
};

function HotLeadsPanel({ hotLeads }) {
  if (!hotLeads) return null;
  const items = hotLeads.items || [];
  return (
    <section className="mt-6 rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-white p-5"
      data-testid="hot-leads-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Flame size={16} className="text-amber-600" />
          <h2 className="font-display text-base font-semibold text-ek-ink">Hot leads · ROI signals</h2>
          <span className="text-[11px] text-ek-ink/65">last {hotLeads.window_days}d</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-ek-ink/70">
          <span className="rounded-full bg-white px-2 py-0.5 ring-1 ring-amber-200" data-testid="hot-leads-downloads">
            <FileDown size={11} className="-mt-0.5 mr-1 inline" />{hotLeads.downloads} PDF downloads
          </span>
          <span className="rounded-full bg-white px-2 py-0.5 ring-1 ring-amber-200" data-testid="hot-leads-estimates">
            {hotLeads.estimates_over_threshold} estimates ≥ {gbp(hotLeads.threshold_gbp)}
          </span>
        </div>
      </div>
      <p className="mt-1 text-xs text-ek-ink/70">
        Visitors whose own ROI numbers cross the £1m threshold, or who downloaded the one-pager. Prioritise them for outreach.
      </p>
      {items.length === 0 ? (
        <p className="mt-3 text-xs text-ek-ink/60" data-testid="hot-leads-empty">No high-value ROI signals yet.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-[10.5px] uppercase tracking-wide text-ek-ink/55">
              <tr>
                <th className="py-1.5 pr-3">When</th>
                <th className="py-1.5 pr-3">Source</th>
                <th className="py-1.5 pr-3">Company</th>
                <th className="py-1.5 pr-3 text-right">Revenue</th>
                <th className="py-1.5 pr-3 text-right">Suppliers</th>
                <th className="py-1.5 pr-3 text-right">Incidents/yr</th>
                <th className="py-1.5 text-right">Annual value</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 12).map((r) => (
                <tr key={r.calc_id} className="border-t border-amber-200/40"
                  data-testid={`hot-lead-row-${r.calc_id}`}>
                  <td className="py-1.5 pr-3 text-ek-ink/70">{relativeTime(r.ts)}</td>
                  <td className="py-1.5 pr-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium ${
                      r.source === "one-pager"
                        ? "bg-ek-purple/10 text-ek-purple"
                        : "bg-amber-100 text-amber-800"
                    }`}>
                      {r.source === "one-pager" ? "PDF download" : "≥£1m estimate"}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-ek-ink">{r.company || <span className="text-ek-ink/45">—</span>}</td>
                  <td className="py-1.5 pr-3 text-right text-ek-ink/80">{gbp(r.revenue)}</td>
                  <td className="py-1.5 pr-3 text-right text-ek-ink/80">{(r.suppliers || 0).toLocaleString()}</td>
                  <td className="py-1.5 pr-3 text-right text-ek-ink/80">{r.incidents}</td>
                  <td className="py-1.5 text-right font-semibold text-ek-purple">{gbp(r.annual_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


const BAND_PILL = {
  critical: "bg-rose-500 text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-amber-400 text-amber-950",
  low: "bg-emerald-500 text-white",
  unknown: "bg-slate-300 text-slate-700",
};

function SrlHotLeadsPanel({ data }) {
  if (!data) return null;
  const items = data.leads || [];
  const threshold = data.threshold || 3;
  return (
    <section
      className="mt-6 rounded-2xl border border-rose-200/70 bg-gradient-to-br from-rose-50 to-white p-5"
      data-testid="srl-hot-leads-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-rose-600" />
          <h2 className="font-display text-base font-semibold text-ek-ink">
            Hot leads · Supplier Risk Lens
          </h2>
          <span className="text-[11px] text-ek-ink/65">
            ≥ {threshold} failing suppliers
          </span>
        </div>
        <span
          className="rounded-full bg-white px-2 py-0.5 text-[11px] text-ek-ink/70 ring-1 ring-rose-200"
          data-testid="srl-hot-leads-total"
        >
          {data.total} matching · top {items.length} shown
        </span>
      </div>
      <p className="mt-1 text-xs text-ek-ink/70">
        Prospects who ran the public lens and just discovered{" "}
        <strong>{threshold}+ failing suppliers</strong> in their own portfolio.
        Direct evidence of acute exposure pain — prioritise for outreach within 24h.
      </p>
      {items.length === 0 ? (
        <p
          className="mt-3 text-xs text-ek-ink/60"
          data-testid="srl-hot-leads-empty"
        >
          No high-exposure lens runs yet. Promote the lens at{" "}
          <code className="rounded bg-white px-1 ring-1 ring-rose-200">
            /supplier-risk-lens
          </code>{" "}
          to start capturing.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-[10.5px] uppercase tracking-wide text-ek-ink/55">
              <tr>
                <th className="py-1.5 pr-3">When</th>
                <th className="py-1.5 pr-3">Email</th>
                <th className="py-1.5 pr-3">Organisation</th>
                <th className="py-1.5 pr-3">Role</th>
                <th className="py-1.5 pr-3">UTM</th>
                <th className="py-1.5 pr-3 text-right">Intent</th>
                <th className="py-1.5 pr-3 text-right">Suppliers</th>
                <th className="py-1.5 pr-3 text-right">Failing</th>
                <th className="py-1.5">Failing band mix</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const md = r.metadata || {};
                const intent = r.intent || {};
                const sc = (r.lens_run || {}).scorecard || {};
                const bc = sc.band_counts || {};
                const failing = md.dissolved_or_failing_count || 0;
                return (
                  <tr
                    key={r.lead_id}
                    className="border-t border-rose-200/40 align-top"
                    data-testid={`srl-hot-lead-row-${r.lead_id}`}
                  >
                    <td className="py-1.5 pr-3 text-ek-ink/70">
                      {relativeTime(r.created_at || r.ts)}
                    </td>
                    <td className="py-1.5 pr-3 text-ek-ink">
                      <a
                        href={`mailto:${r.email}`}
                        className="font-medium text-ek-purple hover:underline"
                      >
                        {r.email}
                      </a>
                    </td>
                    <td className="py-1.5 pr-3 text-ek-ink">
                      {r.company || (
                        <span className="text-ek-ink/45">—</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-ek-ink/80">
                      {r.role || <span className="text-ek-ink/45">—</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-ek-ink/80">
                      {md.utm_source || md.utm_campaign ? (
                        <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-800"
                              data-testid={`srl-utm-${r.lead_id}`}>
                          {[md.utm_source, md.utm_campaign].filter(Boolean).join(" · ")}
                        </span>
                      ) : (
                        <span className="text-ek-ink/45">—</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                          intent.band === "HOT"
                            ? "bg-rose-500 text-white"
                            : intent.band === "WARM"
                              ? "bg-amber-400 text-amber-950"
                              : "bg-slate-200 text-slate-700"
                        }`}
                        data-testid={`srl-intent-band-${r.lead_id}`}
                      >
                        {intent.band} · {intent.intent_score}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-right text-ek-ink/80">
                      {md.input_count || 0}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-semibold text-rose-700">
                      {failing}
                    </td>
                    <td className="py-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        {Object.entries(bc).map(([b, n]) =>
                          n > 0 ? (
                            <span
                              key={b}
                              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${BAND_PILL[b] || BAND_PILL.unknown}`}
                              title={`${b}: ${n}`}
                            >
                              {n}
                            </span>
                          ) : null,
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


function UtmRollupCard({ data }) {
  if (!data) return null;
  const campaigns = data.campaigns || [];
  const noUtm = data.no_utm || { leads: 0, qualifying: 0, conversion_rate: 0 };
  if (campaigns.length === 0 && noUtm.leads === 0) return null;
  return (
    <section
      className="mt-6 rounded-2xl border border-violet-200/70 bg-gradient-to-br from-violet-50 to-white p-5"
      data-testid="utm-rollup-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Flame size={16} className="text-violet-600" />
          <h2 className="font-display text-base font-semibold text-ek-ink">
            Distribution attribution &middot; Supplier Risk Lens
          </h2>
          <span className="text-[11px] text-ek-ink/65">
            last {data.window_days}d
          </span>
        </div>
        <span
          className="rounded-full bg-white px-2 py-0.5 text-[11px] text-ek-ink/70 ring-1 ring-violet-200"
          data-testid="utm-rollup-total"
        >
          {data.total_leads} total leads
        </span>
      </div>
      <p className="mt-1 text-xs text-ek-ink/70">
        Which channel / persona-tag is converting clicks into real intent.
        Conversion = % of leads with <strong>≥ 3 failing suppliers</strong>{" "}
        (the threshold that triggers the hot-leads panel above).
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[10.5px] uppercase tracking-wide text-ek-ink/55">
            <tr>
              <th className="py-1.5 pr-3">Source</th>
              <th className="py-1.5 pr-3">Campaign</th>
              <th className="py-1.5 pr-3 text-right">Leads</th>
              <th className="py-1.5 pr-3 text-right">Qualifying</th>
              <th className="py-1.5 pr-3 text-right">Conv. rate</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c, idx) => (
              <tr
                key={`${c.utm_source}-${c.utm_campaign}`}
                className="border-t border-violet-200/40"
                data-testid={`utm-row-${idx}`}
              >
                <td className="py-1.5 pr-3 text-ek-ink">{c.utm_source}</td>
                <td className="py-1.5 pr-3 text-ek-ink/80">{c.utm_campaign}</td>
                <td className="py-1.5 pr-3 text-right text-ek-ink/80">{c.leads}</td>
                <td className="py-1.5 pr-3 text-right font-semibold text-rose-700">
                  {c.qualifying}
                </td>
                <td className="py-1.5 pr-3 text-right text-ek-ink">
                  {Math.round(c.conversion_rate * 100)}%
                </td>
              </tr>
            ))}
            {noUtm.leads > 0 && (
              <tr
                className="border-t border-violet-200/40 bg-violet-50/50"
                data-testid="utm-no-utm-row"
              >
                <td className="py-1.5 pr-3 text-ek-ink/60 italic">no UTM tag</td>
                <td className="py-1.5 pr-3 text-ek-ink/60 italic">—</td>
                <td className="py-1.5 pr-3 text-right text-ek-ink/60">{noUtm.leads}</td>
                <td className="py-1.5 pr-3 text-right font-semibold text-rose-700">
                  {noUtm.qualifying}
                </td>
                <td className="py-1.5 pr-3 text-right text-ek-ink/60">
                  {Math.round(noUtm.conversion_rate * 100)}%
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-ek-ink/55">
        Tag your inbound links with{" "}
        <code className="rounded bg-white px-1 ring-1 ring-violet-200">
          ?utm_source=&lt;channel&gt;&amp;utm_campaign=&lt;tag&gt;
        </code>{" "}
        to populate this table.
      </p>
    </section>
  );
}



/**
 * iter122 — SOC 2 permanent-grants overdue tile. Daily-rolled by the
 * `/api/docs/permanent-grants-overdue` endpoint. Surfaces the backlog
 * without making the IP-owner open the docs admin panel. Renders as
 * a coloured pill: rose if overdue_count > 0, neutral otherwise.
 */
function PermanentOverduePanel({ data }) {
  const [reloadKey, setReloadKey] = useState(0);
  const [pending, setPending] = useState(null);
  const [refreshed, setRefreshed] = useState(data);

  // Re-fetch after a renewal so the tile shows the new count without
  // forcing the parent to remount.
  useEffect(() => {
    if (reloadKey === 0) return;
    api.get("/docs/permanent-grants-overdue")
      .then(({ data: d }) => setRefreshed(d))
      .catch(() => {});
  }, [reloadKey]);

  const view = refreshed || data;
  if (!view) return null;
  const overdue = view.overdue_count || 0;
  const total = view.total_permanent || 0;
  const top = view.top_overdue || [];
  const cls = overdue > 0
    ? "border-rose-200 bg-rose-50/60"
    : "border-emerald-200 bg-emerald-50/40";
  const ringCls = overdue > 0 ? "text-rose-700" : "text-emerald-700";

  const onRenew14 = async (row) => {
    const next = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
    setPending(`${row.to}|${row.audience}`);
    try {
      await api.post("/docs/grant", {
        email: row.to,
        audience: row.audience,
        expires_at: `${next}T23:59:59+00:00`,
        reason: "Renewed 14d from SOC 2 overdue tile",
      });
      // Also mark the existing permanent-audit row reviewed so the
      // overdue clock resets cleanly. (The renew above creates a NEW
      // grant row with a future expiry; the original permanent row
      // remains and was the one driving the overdue flag, so we
      // mark it reviewed too.)
      try {
        await api.post("/docs/grant-audit/review", {
          to: row.to, audience: row.audience,
        });
      } catch { /* tolerable — re-grant alone updates listing */ }
      toast.success(`Renewed ${row.audience} for ${row.to} (14d expiry).`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Renew failed");
    } finally {
      setPending(null);
    }
  };
  return (
    <section
      className={`rounded-2xl border ${cls} p-5`}
      data-testid="soc2-permanent-overdue-tile"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-ek-ink/65">
          SOC 2 · Permanent grants overdue
        </h3>
        <span className="text-[11px] text-ek-ink/55">
          of {total} total
        </span>
      </div>
      <p
        className={`mt-2 text-4xl font-light tracking-tight ${ringCls}`}
        data-testid="soc2-permanent-overdue-count"
      >
        {overdue}
      </p>
      {overdue === 0 ? (
        <p className="mt-1 text-xs text-ek-ink/65">
          Every permanent grant is within its review window. Good.
        </p>
      ) : (
        <>
          <p className="mt-1 text-xs text-ek-ink/65">
            Permanent grants past their per-audience review threshold.
            Open the Docs admin panel to mark reviewed.
          </p>
          {top.length > 0 && (
            <ul
              className="mt-3 space-y-1 text-[11px] text-ek-ink/75"
              data-testid="soc2-permanent-overdue-top"
            >
              {top.slice(0, 3).map((r, i) => (
                <li
                  key={`${r.to}-${r.audience}-${i}`}
                  className="flex items-baseline justify-between gap-2"
                >
                  <span className="truncate">
                    <b>{r.to}</b>
                    <span className="text-ek-ink/55"> · {r.audience}</span>
                  </span>
                  <span className="flex shrink-0 items-baseline gap-2">
                    <span className="font-semibold text-rose-700">
                      {r.days_since_review}d / {r.threshold_days}d
                    </span>
                    <button
                      type="button"
                      onClick={() => onRenew14(r)}
                      disabled={pending === `${r.to}|${r.audience}`}
                      className="rounded bg-ek-purple px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50"
                      data-testid={`soc2-renew14-${r.audience}-${r.to}`}
                    >
                      {pending === `${r.to}|${r.audience}`
                        ? "Renewing…"
                        : "Renew 14d"}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {Object.keys(data.by_audience || {}).length > 0 && (
        <p className="mt-3 flex flex-wrap gap-1 text-[10px] text-ek-ink/55">
          {Object.entries(data.by_audience).map(([aud, count]) => (
            <span
              key={aud}
              className="rounded-full bg-white px-1.5 py-0.5 ring-1 ring-rose-200"
            >
              {aud}: <b>{count}</b>
            </span>
          ))}
        </p>
      )}
    </section>
  );
}


/**
 * PostProvisionCard — shown right after `POST /api/tenants` succeeds.
 * Mirrors the artefacts the `scripts/provision_pilot.py` CLI produces:
 *   • temp admin password (revealable + one-click copy),
 *   • starter-pack rows cloned,
 *   • magic-link bootstrap email status,
 *   • Markdown "Pilot brief" download (calls `/api/owner/tenants/{id}/pilot-brief.md`
 *     and forces a file download via blob + ObjectURL so no server-side
 *     state mutation is needed).
 */
function PostProvisionCard({ ctx, onDismiss }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(ctx.admin_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard blocked — reveal and copy manually.");
    }
  };

  const downloadBrief = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({
        admin_email: ctx.admin_email,
        admin_password: ctx.admin_password,
        admin_name: ctx.admin_name || "",
        bootstrap_sent: String(!!ctx.bootstrap_email?.sent),
        magic_link: ctx.bootstrap_email?.magic_link || "",
        starter_controls: String(ctx.starter_pack?.controls || 0),
        starter_kris: String(ctx.starter_pack?.kris || 0),
      });
      const url = `/owner/tenants/${encodeURIComponent(ctx.tenant_id)}/pilot-brief.md?${params}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/markdown" });
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `pilot-brief-${ctx.tenant_id}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Brief download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6"
      data-testid="post-provision-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">
              {ctx.name} provisioned
            </h2>
            <p className="mt-1 text-sm text-ek-ink/70">
              Hand the temp password to the customer admin and download the
              pilot brief — same artefact the <code>scripts/provision_pilot.py</code>{" "}
              CLI generates.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-ek-ink/60 hover:text-ek-ink"
          data-testid="post-provision-dismiss"
        >
          Dismiss
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-black/5 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-ek-ink/55">Tenant id</p>
          <p className="mt-1 font-mono text-sm text-ek-ink" data-testid="post-provision-tenant-id">
            {ctx.tenant_id}
          </p>
        </div>
        <div className="rounded-xl border border-black/5 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-ek-ink/55">Admin email</p>
          <p className="mt-1 text-sm text-ek-ink" data-testid="post-provision-admin-email">
            {ctx.admin_email}
          </p>
        </div>
        <div className="rounded-xl border border-black/5 bg-white p-3 sm:col-span-2">
          <p className="text-[11px] uppercase tracking-wide text-ek-ink/55">
            Temporary password{" "}
            <span className="font-normal text-ek-ink/45">
              (force-reset on first login)
            </span>
          </p>
          <div className="mt-1 flex items-center gap-2">
            <code
              className="flex-1 truncate rounded-md bg-black/5 px-2 py-1.5 font-mono text-sm text-ek-ink"
              data-testid="post-provision-password"
            >
              {revealed ? ctx.admin_password : "•".repeat(ctx.admin_password.length)}
            </code>
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              className="rounded-md border border-black/10 px-2 py-1.5 text-xs font-semibold text-ek-ink hover:border-ek-purple/40 hover:text-ek-purple"
              data-testid="post-provision-reveal"
            >
              {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={copyPw}
              className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1.5 text-xs font-semibold text-ek-ink hover:border-ek-purple/40 hover:text-ek-purple"
              data-testid="post-provision-copy"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <div className="rounded-xl border border-black/5 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-ek-ink/55">Starter pack</p>
          <p className="mt-1 text-sm text-ek-ink">
            {ctx.starter_pack?.controls || 0} controls · {ctx.starter_pack?.kris || 0} KRIs · risk-appetite defaults
          </p>
        </div>
        <div className="rounded-xl border border-black/5 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-ek-ink/55">Magic-link email</p>
          <p className="mt-1 text-sm" data-testid="post-provision-bootstrap-status">
            {ctx.bootstrap_email?.sent ? (
              <span className="text-emerald-700">Sent to {ctx.admin_email}</span>
            ) : (
              <span className="text-amber-700">
                Not sent — admin must use the temp password above.
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={downloadBrief}
          disabled={downloading}
          className="ek-btn flex items-center gap-1.5"
          data-testid="post-provision-download-brief"
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
          Download pilot brief (Markdown)
        </button>
        <span className="text-xs text-ek-ink/55">
          The brief includes the Day-1 acceptance checklist, off-boarding steps, and Sentry tag guidance.
        </span>
      </div>
    </section>
  );
}



// ---------------------------------------------------------------------------
// ConfigureTenantDrawer — wraps TenantConfigPanel in a right-side drawer
// matching the look of ManageTenantDialog.
// ---------------------------------------------------------------------------
function ConfigureTenantDrawer({ tenant, onClose }) {
  if (!tenant) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30"
      onClick={onClose}
      data-testid="tenant-config-drawer"
    >
      <div
        className="ml-auto h-full w-full max-w-3xl overflow-y-auto bg-ek-canvas shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black/5 bg-white/95 px-6 py-3 backdrop-blur">
          <div>
            <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-ek-ink/60">
              Tenant configuration
            </h2>
            <p className="font-mono text-xs text-ek-ink/50">{tenant.tenant_id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-black/10 bg-white px-3 py-1 text-xs font-semibold text-ek-ink hover:bg-ek-purple/5"
            data-testid="tenant-config-drawer-close"
          >
            Close
          </button>
        </div>
        <div className="px-6 pb-10">
          <TenantConfigPanel tenant={tenant} />
        </div>
      </div>
    </div>
  );
}
